import React, { useState } from 'react';
import Page from 'components/UI/Page';
import BusinessUnitUsersTable from './BusinessUnitUsersTable';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { useBusinessUnitUsers } from 'hooks/useBusinessUnitUsers';
import { useAuth } from 'lib/Auth';
import Modal from 'components/UI/Modal';
import { Main } from 'styles/themeColors';
import { BUSINESS_UNIT_USERS_COLUMNS_ORG, USER_FIELDS, USERS_BUSINESS_UNIT_CONSTANTS } from 'constants/usersBusinessUnit';
import Vendor from 'data/Vendor';
import { USER_ROLE_MAPPER } from 'constants/user';
import SearchComponent from 'components/UI/SearchComponent';
import { GENERAL_CONSTANTS } from 'constants/general';
import { useUserDeletion } from 'hooks/useUserDeletion';
import styled from 'styled-components';
import { BusinessUnit } from 'data/BusinessUnit';
import { isVendorOnlyByBusinessUnit } from 'utils/role';
import { useBusinessUnitBrandFiltering } from 'hooks/useBusinessUnitBrandFiltering';

const FilterRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    margin-bottom: 16px;
    gap: 16px;
    
    > div {
        width: 25%;
    }
`;

interface Props {
    selectedVendor: Vendor;
    selectedBusinessUnit?: BusinessUnit | null;
    toggleInviteModal: any;
    setUserProfileId: any;
    handleInviteInputChange: any;
    reload: boolean;
    setReload: any;
}

const AllUsersTab: React.FC<Props> = ({ selectedVendor, selectedBusinessUnit: globalSelectedBusinessUnit, toggleInviteModal, setUserProfileId, handleInviteInputChange, reload, setReload }) => {
    const { user } = useAuth();
    // const [selectedBusinessUnit, setSelectedBusinessUnit] = useState<string>(GENERAL_CONSTANTS.ALL);

    // Determine vendor-only status based on global business unit context
    const isVendorOnlyUser = isVendorOnlyByBusinessUnit(user!, globalSelectedBusinessUnit);

    // Get business unit IDs for filtering when "All" is selected
    const { businessUnitsIds } = useBusinessUnitBrandFiltering({
        selectedBusinessUnit: globalSelectedBusinessUnit || undefined,
        selectedVendorId: selectedVendor?.id
    });

    // Convert businessUnitsIds array to comma-separated string for API
    const businessUnitsIdsString = businessUnitsIds.length > 0 ? businessUnitsIds.join(',') : undefined;

    // Centralized state management using the useBusinessUnitUsers hook
    const {
        tableData,
        loading,
        currentPage,
        rowsPerPage,
        sortBy,
        sortOrder,
        hasInitialLoad,
        tableKey,
        selectedRows,
        // Actions
        setSelectedRows,
        handlePageChange,
        handleRowsPerPageChange,
        handleSortChange,
        handleRemove,
        handleDeleteMultiple,
        searchTerm,
        setSearchTerm
    } = useBusinessUnitUsers({
        reload,
        setReload,
        vendorId: selectedVendor?.id,
        businessUnitId: globalSelectedBusinessUnit?.id?.toString(),
        businessUnitsIds: !globalSelectedBusinessUnit?.id ? businessUnitsIdsString : undefined
    });

    // User deletion logic using the custom hook
    const {
        showDeleteModal,
        handleRemoveUser,
        handleBulkRemoveUser,
        handleProfileDeletion,
        onCloseDeleteModal
    } = useUserDeletion({ handleRemove, handleDeleteMultiple });

    // Custom handlers that can be extended with business logic
    const handleEditUser = (userProfileId: number) => {
        toggleInviteModal();
        setUserProfileId(userProfileId);
        if (tableData?.data?.length > 0) {
            const user = tableData.data.find((u) => u.id === userProfileId);
            if (user) {
                handleInviteInputChange(USER_FIELDS.NAME as any, user.name || '');
                handleInviteInputChange(USER_FIELDS.EMAIL as any, user.email || '');
                handleInviteInputChange(USER_FIELDS.USER_ROLE as any, { id: user.role, value: USER_ROLE_MAPPER[user.role] });
                handleInviteInputChange(USER_FIELDS.BUSINESS_UNIT as any, user.businessUnitId);
                handleInviteInputChange(USER_FIELDS.VENDOR as any, user.vendorId);
                handleInviteInputChange(USER_FIELDS.GIN_ID as any, user.ginId || '');
            }
        }
    }

    // Show loading state during initial load
    if (loading && !hasInitialLoad) {
        return (
            <Page>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <LoadingIndicator />
                </div>
            </Page>
        );
    }

    return (
        <>
            <FilterRow>
                <div>
                    <SearchComponent
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        placeHolder={GENERAL_CONSTANTS.SEARCH_EMAIL_USERNAME}
                        padding={'0'}
                        useGrid={false}
                    />
                </div>

            </FilterRow>

            {/* Pass all state and handlers to the table component */}
            <BusinessUnitUsersTable
                // Data props
                data={tableData.data}
                totalCount={tableData.totalCount}
                loading={loading}
                tableKey={tableKey}
                columns={BUSINESS_UNIT_USERS_COLUMNS_ORG}

                // Pagination props
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}

                // Sorting props
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSortChange={handleSortChange}

                // Selection props
                selectedRows={selectedRows}
                onSelectionChange={setSelectedRows}

                // Action handlers
                onEdit={handleEditUser}
                onRemove={handleRemoveUser}
                onRemoveBulkUser={() => handleBulkRemoveUser(selectedRows)}

                // UI state
                isVendorOnly={isVendorOnlyUser}
                hasInitialLoad={hasInitialLoad}
            />

            <Modal
                showModal={showDeleteModal}
                onClose={onCloseDeleteModal}
                title={GENERAL_CONSTANTS.CONFIRM_DELETE}
                primaryButtonText={GENERAL_CONSTANTS.DELETE}
                ghostButtonText={GENERAL_CONSTANTS.CANCEL}
                primaryButtonColor={Main.brandDark}
                color={Main.black}
                borderColor={Main.disabled}
                onClick={handleProfileDeletion}
            >
                {USERS_BUSINESS_UNIT_CONSTANTS.DELETE_USER_PROFILE}
            </Modal>
        </>
    );
};

export default AllUsersTab;